export const useFormDialogStore = defineStore('formDialog', () => {
  const DEFAULT_DIALOG_WIDTH = 500;
  const formDialogTitle = ref('');
  const formDialogInstructions = ref('');
  const formDialogWidth = ref<string | number>(DEFAULT_DIALOG_WIDTH);
  const formDialogDense = ref(false);
  const formDialogFields = ref<FormConfig[]>([]);
  const formDialogInitialData = ref<GenericFormData | undefined>();
  const formDialogOpen = ref(false);

  let formDialogPromise: Promise<unknown | null> | undefined;
  let resolveFormDialogPromise: ((data: GenericFormData | null) => Promise<void | FormErrors>) | undefined;

  /**
   * Given a title, fields, and initial data, opens the form dialog and
   * sets up the promise in the background so that `setFormDialogResponse`
   * will resolve it and close the dialog.
   *
   * Returns the promise set up behind the scenes, which can be
   * `await`ed to wait for the user's response before taking action.
   * The promise should resolve to `null` if the user clicks the "Cancel"
   * button, otherwise it will resolve to the contents of the form.
   *
   * If the third argument is provided, the dialog will call the async
   * function on form submit and waits for it to successfully resolve before
   * closing the dialog. If the promise from this function is rejected,
   * the dialog will assign the rejected value as form errors. This should
   * display errors next to the related fields if the rejected value is
   * structured as a FormError type.
   *
   * @example
   * const getSomeFormData = async () => {
   *   const data = await openFormDialog('Set Greeting', { fields: formFields });
   *   doSomethingWithFormData(data);
   * };
   *
   * @example
   * const getSomeFormData = async () => {
   *   const result = await openFormDialog('Update Resource', { fields: formFields }, {
   *     onSubmit: async data => {
   *       try {
   *         return await makePostRequest(data);
   *       }
   *       catch (err) {
   *         // Assuming this is a 400 error, this probably contains errors for the
   *         throw (err as FetchError).data
   *       }
   *     }
   *   });
   * };
   *
   * @param title
   * @param formConfig
   * @param onSubmitFunc
   */
  const openFormDialog = <T>(
    title: string,
    formConfig: Parameters<typeof useSetupFormFromFields>[0],
    dialogConfig: {
      width?: number | string
      dense?: boolean
      onSubmit?: (data: GenericFormData) => Promise<T | FormErrors>
    },
    instructions?: string,
  ): Promise<GenericFormData | T | null> => {
    formDialogTitle.value = title;
    formDialogInstructions.value = instructions ?? '';
    formDialogWidth.value = dialogConfig?.width || DEFAULT_DIALOG_WIDTH;
    formDialogDense.value = Boolean(dialogConfig?.dense);
    formDialogFields.value = formConfig.fields;
    formDialogInitialData.value = formConfig.initialData;
    formDialogOpen.value = true;
    formDialogPromise = new Promise(resolve => {
      resolveFormDialogPromise = async data => {
        if (data !== null) {
          // If there is a submit function, try awaiting that (and let errors bubble up)
          const submitResponse = dialogConfig?.onSubmit ? (await dialogConfig.onSubmit(data)) : data;
          // If no errors, resolve and close the dialog
          resolve(submitResponse as T);
          formDialogPromise = undefined;
          resolveFormDialogPromise = undefined;
        }
        else {
          resolve(data);
          formDialogPromise = undefined;
          resolveFormDialogPromise = undefined;
        }
      };
    });

    return formDialogPromise as Promise<T | null>;
  };

  /**
   * If there's an existing promise for the form dialog,
   * resolve it with the given value, then close the dialog and
   * clear the message ref.
   *
   * @param response
   */
  const setFormDialogResponse = async (data: GenericFormData | null): Promise<void | FormErrors> => {
    if (!resolveFormDialogPromise) {
      return;
    }

    await resolveFormDialogPromise(data);
    formDialogOpen.value = false;
    // Wait until the dialog is closed, then clear the message
    setTimeout(() => {
      formDialogTitle.value = '';
      formDialogFields.value = [];
      formDialogInitialData.value = undefined;
    }, 200);
  };

  return {
    formDialogTitle,
    formDialogInstructions,
    formDialogWidth,
    formDialogDense,
    formDialogFields,
    formDialogInitialData,
    formDialogOpen,
    openFormDialog,
    setFormDialogResponse,
  };
});
